import { Grid, Paper, Typography, useMediaQuery } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import ConsumptionsManagementsDeliveryAddressComponent from "../../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import theme from "../../../muiTheme";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import EmptyStateComponent from "../../UI/EmptyStateComponent/EmptyStateComponent";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import {
  FormattedComparativeConsumptions,
  FormattedConsumptionList,
  GetGraphDataRequestBody,
  SelectedDataComparativeConsumptions,
} from "../../../core/models/consumptions/consumptions.model";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import "./ConsumptionsComparative.scss";
import { getGraphDataComparativeConsumptions } from "../../../core/services/consumptions/consumptions.service";
import { UserData } from "../../../core/models/login/login.model";
import { ActiveElement, Chart, ChartEvent } from "chart.js";
import {
  formatComparativeConsumptions,
  getBarPositionX,
  getLineChart,
  handleClickLineChart,
} from "../../../utils/components/consumptions/consumptions.util";

interface ConsumptionsComparativeProps {
  ownerDataCups: OwnerDataCups[];
  userData: UserData | null;
  ownerDataCupSelected: OwnerDataCups;
  setOwnerDataCupSelected: React.Dispatch<React.SetStateAction<OwnerDataCups>>;
  consumptionsList: FormattedComparativeConsumptions[];
  setConsumptionsList: React.Dispatch<
    React.SetStateAction<FormattedComparativeConsumptions[]>
  >;
  selectedDataPeriod: SelectedDataComparativeConsumptions;
  setSelectedDataPeriod: React.Dispatch<
    React.SetStateAction<SelectedDataComparativeConsumptions>
  >;
}

const ConsumptionsComparative = ({
  ownerDataCups,
  userData,
  ownerDataCupSelected,
  setOwnerDataCupSelected,
  consumptionsList,
  setConsumptionsList,
  selectedDataPeriod: { elements, consumptions, positionX },
  setSelectedDataPeriod,
}: ConsumptionsComparativeProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [localOwnerDataCupSelected, setLocalOwnerDataCupSelected] =
    useState<OwnerDataCups>(ownerDataCupSelected);
  const [isFirstChartLoadFlag, setIsFirstChartLoadFlag] =
    useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chart = useRef<Chart | null>(null);
  const isDesktopViewport = useMediaQuery("(min-width: 1100px )");
  const periods = ["P1", "P2", "P3", "P4", "P5", "P6"];
  const [environment, setEnvironment] = useState<'Gas' | 'Luz'>('Luz');

  useEffect(() => {
    if (canvasRef.current) {
      chart.current = getLineChart(
        environment,
        canvasRef.current,
        consumptionsList,
        isDesktopViewport,
        (elements: ActiveElement[], event: ChartEvent) => {
          handleClickLineChart(elements, chart.current as Chart);
          setSelectedDataPeriod({
            consumptions: consumptionsList.map(
              ({ consumptionList: list }) => list[elements[0].index]
            ),
            positionX: getBarPositionX(chart.current as Chart, event),
            elements,
          });
        }
      );

      return () => {
        (chart.current as Chart).destroy();
      };
    }
  }, [consumptionsList, isDesktopViewport, setSelectedDataPeriod]);

  useEffect(() => {
    if (isFirstChartLoadFlag && chart.current && elements.length) {
      handleClickLineChart(elements, chart.current as Chart);
      setIsFirstChartLoadFlag(false);
    }
  }, [elements, isFirstChartLoadFlag]);

  const handleClickSearch = (localOwnerDataCupSelected: OwnerDataCups) => {
    setIsLoading(true);
    setOwnerDataCupSelected(localOwnerDataCupSelected);
    setEnvironment(localOwnerDataCupSelected.envirorment === "Gas" ? "Gas" : "Luz");
    const actualYear = new Date().getFullYear();
    const getGraphDataRequestBody: GetGraphDataRequestBody = {
      cupsId: localOwnerDataCupSelected.idCups.toString(),
      dateFrom: (actualYear - 1).toString(),
      dateTo: actualYear.toString(),
    };

    getGraphDataComparativeConsumptions(
      getGraphDataRequestBody,
      userData?.token || ""
    )
      .then((graphData) => {
        setConsumptionsList(formatComparativeConsumptions(graphData));
        setSelectedDataPeriod((prevSelectedDataPeriod) => ({
          consumptions: [],
          positionX: null,
          elements: [],
        }));
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  return (
    <Stack component="article" rowGap={"2rem"} padding={"2rem 0"}>
      <Paper sx={{ width: "100%", borderRadius: "12px" }}>
        <Stack
          padding="2rem"
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-between"}
          alignItems={"start"}
          gap={"2rem"}
          flexWrap={"wrap"}
        >
          <Typography component="p" variant="body1">
            {t("Consumos.Selecciona el CUPS que desee comparar con respecto a los consumos del año anterior")}
          </Typography>
          <Stack
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            alignItems={"end"}
            gap={"2rem"}
            flexWrap={"wrap"}
          >
            <Stack width={{xs:"100%", md:"22rem"}}>
              <ConsumptionsManagementsDeliveryAddressComponent
                ownerDataCups={ownerDataCups}
                ownerDataCupSelected={localOwnerDataCupSelected}
                setOwnerDataCupSelected={(ownerDataCupSelected: OwnerDataCups) =>
                  setLocalOwnerDataCupSelected(ownerDataCupSelected)
                }
              />
            </Stack>
            <ButtonElement
              onClick={() => handleClickSearch(localOwnerDataCupSelected)}
              disabled={!!!localOwnerDataCupSelected.codeCups.length}
            >
              {t(`Comparar`)}
            </ButtonElement>
          </Stack>
        </Stack>
      </Paper>
      {!isLoading ? (
        <>
          {!!consumptionsList.length ? (
            <Paper sx={{ width: "100%", borderRadius: "12px" }}>
              <Stack component="div">
                <Box>
                  <canvas
                    id="chart"
                    ref={canvasRef}
                    style={{ height: "600px" }}
                  />
                </Box>
                <Stack
                  component="div"
                  height={"auto"}
                  bgcolor={theme.palette.grey[50]}
                  border={`1px solid ${theme.palette.grey[300]}`}
                  borderRadius={"12px"}
                  margin={{xs:"1rem", md:"3rem"}}
                  paddingY={"3rem"}
                  paddingX={{ xs: "1.5rem", md: "6rem" }}
                  position={"relative"}
                  gap={{xs:"3rem", md:"2.5rem"}}
                >
                  {consumptions.length ? (
                    <>
                      <Box
                        component={"div"}
                        position={"absolute"}
                        bgcolor={theme.palette.grey[50]}
                        sx={{
                          borderTop: `1px solid ${theme.palette.grey[300]}`,
                          borderLeft: `1px solid ${theme.palette.grey[300]}`,
                        }}
                        width={"20px"}
                        height={"20px"}
                        top={-11}
                        left={positionX ? positionX - 60 : 0}
                        className="arrow-graph"
                      />
                      {consumptions.map((consumption, indexNumber) => (
                        <Stack
                          key={`${consumption.invoiceDate}_${indexNumber}`}
                          borderBottom={
                            !!!indexNumber
                              ? `1px solid ${theme.palette.grey[300]}`
                              : ""
                          }
                          paddingBottom={
                            !!!indexNumber
                              ? {xs:"3rem", md:"2.5rem"}
                              : ""
                          }
                        >
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={"0.5rem"}
                          >
                            <Typography
                              component={"span"}
                              width={"10px"}
                              height={"10px"}
                              borderRadius={"50px"}
                              bgcolor={
                                theme.palette.consumptions[
                                  !!!indexNumber ? "period1" : "period2"
                                ].main
                              }
                            />
                            <Typography component={"p"} variant={"subtitle1"}>
                              {`Periodo ${indexNumber + 1} (${
                                consumption.year
                              })`}
                            </Typography>
                          </Box>
                          <Grid
                            container
                            columnSpacing={{ xs: 12, sm: 8, md: 6 }}
                            
                          >
                            <Grid item paddingTop="1rem">
                              <Typography
                                component={"p"}
                                variant="body1"
                                color={theme.palette.grey[500]}
                              >
                                {t(`Consumos.Energía Activa`)}
                              </Typography>
                              <Typography
                                component={"p"}
                                variant="subtitle1"
                              >{`${formatNumber(
                                consumption.totalActiveConsumption || 0
                              )} ${environment === 'Gas' ? 'm3' : 'kWh'}`}</Typography>
                            </Grid>
                            {periods.map((period) => {
                              const key =
                                `active${period}` as keyof FormattedConsumptionList;
                              return (
                                <Grid item key={period} paddingTop="1rem">
                                  <Typography
                                    component={"p"}
                                    variant="body1"
                                    color={theme.palette.grey[500]}
                                  >
                                    {period}
                                  </Typography>
                                  <Typography
                                    component={"p"}
                                    variant="subtitle1"
                                  >{`${formatNumber(
                                    (consumption[key] as number) || 0
                                  )} ${environment === 'Gas' ? 'm3' : 'kWh'}`}</Typography>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                      ))}
                    </>
                  ) : (
                    <Stack
                      display={"flex"}
                      flex-direction={"column"}
                      alignContent={"center"}
                      alignItems={"center"}
                      rowGap={2}
                    >
                      <Typography component={"span"} variant="h3" className="t-icon-select round-icon"></Typography>
                      <Typography
                        component={"p"}
                        variant={"subtitle2"}
                        color={theme.palette.grey[700]}
                        margin={"auto"}
                      >
                        {t(
                          `Consumos.Selecciona uno de los puntos de la gráfica para comparar el consumo`
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <EmptyStateComponent
              text={"Consumos.Selecciona un suministro"}
              height={"30rem"}
            />
          )}
        </>
      ) : (
        <Paper
          sx={{
            width: "100%",
            borderRadius: "12px",
            height: "30rem",
            position: "relative",
          }}
        >
          <LoadingComponentComponent isLoading={isLoading} />
        </Paper>
      )}
    </Stack>
  );
};

export default ConsumptionsComparative;
